import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pdApi, type PdFramework } from '../api';
import './FrameworksList.css';
import { HelpPanel } from '../HelpPanel';
import { FRAMEWORKS_LIST } from '../helpContent';

/**
 * Settings → Assessment Framework → Frameworks.
 *
 * CLONED from `/Users/glucic/src/ai_readiness_template/views/frameworks/list.ejs`, screen
 * title "Framework Management". Layout, column set, chip wording, button colours, modal
 * copy and empty state are the source's.
 *
 * WHAT THE SCREEN IS FOR: a framework is only safe to score against once every component
 * carries at least one question, and only safe to CHANGE until someone has scored against
 * it. Those two facts drive everything visible here —
 *
 *   * the COMPLETENESS bar, green only at 100%, is the gate on "Complete";
 *   * the LEFT RAIL and the status chip say which of the three states a row is in;
 *   * each action is a different colour because they are different kinds of act — Clone
 *     is safe, Delete is not, and they sit next to each other.
 *
 * THE ACTION SET — owner-directed 2026-08-10, no longer the source's conditions:
 *
 *   Designer  NOT locked          "if the framework is locked … there is no way that we
 *                                  should build or rebuild that framework"
 *   Edit      NOT locked          }  "everything should be possible as long as the
 *   Delete    NOT locked, not default }  framework is not locked"
 *   Clone     always
 *   Complete  not locked, not complete, completeness 100%
 *   Revert    complete AND adopted by no project
 *
 * LOCKED IS THE ONLY GATE THAT CLOSES THINGS DOWN. The source additionally gated Edit and
 * Delete on `Design Complete`, which meant a finished-but-unadopted framework silently lost
 * both — the owner found exactly that on AIRDY-V01 and it was not a state he recognised.
 * Completion is a milestone; ADOPTION is what forecloses change, and adoption is what the
 * lock represents.
 *
 * Two independent things set `is_locked`, and forgetting the second one wastes an hour:
 *   1. marking a framework Complete (settings.ts, owner-directed today), and
 *   2. ANY scoring form being opened against it (scoring.ts:243) — pre-existing, and the
 *      reason AIRDY-V01 arrived from the source already locked.
 */

type ModalKind = 'create' | 'clone' | 'edit' | null;

export function FrameworksList() {
  const navigate = useNavigate();
  const [frameworks, setFrameworks] = useState<PdFramework[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modal, setModal] = useState<ModalKind>(null);
  const [form, setForm] = useState({ id: '', name: '', description: '', sourceName: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pdApi.settings.frameworks();
      setFrameworks(data.frameworks);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      setModal(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /** Percent of components carrying at least one question, rounded — the source's formula. */
  const completenessOf = (f: PdFramework): number =>
    Number(f.total_components) > 0
      ? Math.round((Number(f.components_with_questions) / Number(f.total_components)) * 100)
      : 0;

  const barColour = (pct: number) => (pct === 100 ? '#4caf50' : pct >= 50 ? '#ff9800' : '#f44336');

  const openCreate = () => {
    setForm({ id: '', name: '', description: '', sourceName: '' });
    setModal('create');
  };

  return (
    <div className="fw-page">
      <div className="fw-fixed">
        <div className="fw-page-header">
          <div className="fw-header-content">
            <div>
              <h1 className="fw-title">Framework Management</h1>
              <p className="fw-subtitle">Create and manage multiple assessment frameworks</p>
            </div>
            <div className="pd-page-header__actions">
              <HelpPanel help={FRAMEWORKS_LIST} />
            </div>
          </div>
        </div>

        <div className="fw-quick">
          <button
            type="button"
            className="fw-create"
            title="Create a new framework"
            onClick={openCreate}
          >
            <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Create Framework
          </button>
          <span className="fw-count">
            {frameworks.length} framework{frameworks.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <div className="fw-scroll">
        {error ? <div className="fw-error">{error}</div> : null}

        {loading ? (
          <div className="fw-empty">
            <p>Loading…</p>
          </div>
        ) : frameworks.length === 0 ? (
          <div className="fw-empty">
            <div className="fw-empty-icon">🎯</div>
            <h3>No Frameworks Found</h3>
            <p>Create your first framework to get started</p>
            <button type="button" onClick={openCreate}>
              + Create Framework
            </button>
          </div>
        ) : (
          <div className="fw-card">
            <div className="fw-card-content">
              <table className="fw-table">
                <thead>
                  <tr>
                    <th>Framework</th>
                    <th className="fw-center">Status</th>
                    <th className="fw-center">Dim</th>
                    <th className="fw-center">Comp</th>
                    <th className="fw-center">Q&apos;s</th>
                    <th className="fw-completeness-col">Completeness</th>
                    <th className="fw-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {frameworks.map((f) => {
                    const pct = completenessOf(f);
                    const locked = Boolean(f.is_locked);
                    const complete = f.design_status === 'Design Complete';
                    /*
                     * ADOPTION, NOT COMPLETION, IS WHAT MAKES A FRAMEWORK PERMANENT.
                     * Owner-directed, 2026-08-10 — Complete now also locks, so the old
                     * `!locked && complete` test for Revert could never be true and the
                     * button would have vanished. What actually forecloses a change is a
                     * project having adopted the framework.
                     */
                    const adopted = Number(f.direct_project_count ?? 0);
                    const rowClass = locked
                      ? 'fw-row-locked'
                      : complete
                        ? 'fw-row-complete'
                        : 'fw-row-in-design';

                    return (
                      <tr key={f.framework_id} className={rowClass}>
                        <td>
                          <div className="fw-name">{f.framework_name}</div>
                          <div className="fw-id">{f.framework_id}</div>
                          {f.framework_description ? (
                            <div className="fw-desc" title={String(f.framework_description)}>
                              {f.framework_description}
                            </div>
                          ) : null}
                        </td>

                        <td className="fw-center">
                          {locked ? (
                            <span className="fw-status fw-status-locked">LOCKED</span>
                          ) : complete ? (
                            <span className="fw-status fw-status-complete">COMPLETE</span>
                          ) : (
                            <span className="fw-status fw-status-design">IN DESIGN</span>
                          )}
                        </td>

                        <td className="fw-num">{Number(f.total_dimensions ?? 0)}</td>
                        <td className="fw-num">{Number(f.total_components ?? 0)}</td>
                        <td className="fw-num">{Number(f.total_questions ?? 0)}</td>

                        <td>
                          <div className="fw-completeness">
                            <div className="fw-bar">
                              <div
                                className="fw-bar-fill"
                                style={{ width: `${pct}%`, background: barColour(pct) }}
                              />
                            </div>
                            <span
                              className="fw-pct"
                              style={{ color: pct === 100 ? '#4caf50' : '#666' }}
                            >
                              {pct}%
                            </span>
                          </div>
                        </td>

                        <td className="fw-actions-cell">
                          <div className="fw-actions">
                            {/*
                             * A LOCKED FRAMEWORK OFFERS NO BUILDER. Owner-directed,
                             * 2026-08-10: *"if the framework is locked, there should be no
                             * more building that framework ... because it's already used
                             * there's nothing more to build there."*
                             *
                             * The Builder disabled every one of its own controls when
                             * locked, so the button led to a screen where nothing could be
                             * done — which reads as a broken screen rather than a closed
                             * one. Removing the entry point says the same thing honestly.
                             *
                             * CONSEQUENCE WORTH KNOWING: the Builder is also the only place
                             * a framework's dimensions and questions can be INSPECTED, so a
                             * locked framework's content is now unreachable from this
                             * screen. Raised with the owner; a read-only Builder is the fix
                             * if he wants the visibility back.
                             */}
                            {!locked ? (
                              <button
                                type="button"
                                className="fw-btn fw-btn-designer"
                                title="Open in Framework Builder"
                                onClick={() =>
                                  navigate(
                                    `/portfolio_definition/settings/assessment-framework/framework-builder?framework=${encodeURIComponent(f.framework_id)}`,
                                  )
                                }
                              >
                                Designer
                              </button>
                            ) : null}

                            {/*
                             * LOCKED IS THE ONLY GATE. Owner-directed, 2026-08-10: *"I
                             * believe that everything should be possible as long as the
                             * framework is not locked."*
                             *
                             * These were gated on `!locked && !complete`, so a framework
                             * that was Design Complete but unlocked — which is exactly what
                             * AIRDY-V01 is — lost Edit and Delete for no reason the owner
                             * recognised. Completion is a milestone; adoption is what
                             * actually forecloses change, and that is what sets the lock.
                             */}
                            {!locked ? (
                              <button
                                type="button"
                                className="fw-btn fw-btn-edit"
                                title="Edit"
                                onClick={() => {
                                  setForm({
                                    id: f.framework_id,
                                    name: f.framework_name,
                                    description: String(f.framework_description ?? ''),
                                    sourceName: '',
                                  });
                                  setModal('edit');
                                }}
                              >
                                Edit
                              </button>
                            ) : null}

                            <button
                              type="button"
                              className="fw-btn fw-btn-clone"
                              title="Clone"
                              onClick={() => {
                                setForm({
                                  id: f.framework_id,
                                  name: '',
                                  description: '',
                                  sourceName: f.framework_name,
                                });
                                setModal('clone');
                              }}
                            >
                              Clone
                            </button>

                            {!locked && !complete && pct === 100 ? (
                              <button
                                type="button"
                                className="fw-btn fw-btn-complete"
                                title="Mark Complete"
                                onClick={() => run(() => pdApi.settings.markDesignComplete(f.framework_id))}
                              >
                                Complete
                              </button>
                            ) : null}

                            {complete && adopted === 0 ? (
                              <button
                                type="button"
                                className="fw-btn fw-btn-revert"
                                title="Revert to In Design — unlocks the framework for editing"
                                onClick={() => run(() => pdApi.settings.revertToInDesign(f.framework_id))}
                              >
                                Revert
                              </button>
                            ) : null}
                            {complete && adopted > 0 ? (
                              <span
                                className="fw-inuse"
                                title={`Assigned to ${adopted} project(s). Remove it from those projects before it can be changed.`}
                              >
                                In use by {adopted} project{adopted === 1 ? '' : 's'}
                              </span>
                            ) : null}

                            {!f.is_default && !locked ? (
                              <button
                                type="button"
                                className="fw-btn fw-btn-delete"
                                title="Delete"
                                onClick={() => {
                                  if (
                                    !window.confirm(
                                      `Delete framework "${f.framework_name}"? This cannot be undone.`,
                                    )
                                  )
                                    return;
                                  return run(() => pdApi.settings.deleteFramework(f.framework_id));
                                }}
                              >
                                Delete
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ---- Create ---- */}
      {modal === 'create' ? (
        <div className="fw-modal-backdrop" onClick={() => setModal(null)}>
          <div className="fw-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Framework</h3>
            <div className="fw-modal-field">
              <label htmlFor="fw-create-name">Framework Name *</label>
              <input
                id="fw-create-name"
                type="text"
                placeholder="e.g., Healthcare Assessment Framework"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="fw-modal-field">
              <label htmlFor="fw-create-desc">Description</label>
              <textarea
                id="fw-create-desc"
                placeholder="Optional description..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="fw-modal-actions">
              <button type="button" className="fw-modal-cancel" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="fw-modal-confirm fw-modal-confirm-create"
                onClick={() =>
                  run(() =>
                    pdApi.settings.createFramework({
                      framework_name: form.name,
                      framework_description: form.description,
                    }),
                  )
                }
              >
                Create Framework
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ---- Clone ---- */}
      {modal === 'clone' ? (
        <div className="fw-modal-backdrop" onClick={() => setModal(null)}>
          <div className="fw-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Clone Framework</h3>
            <div className="fw-clone-source">
              <div className="fw-clone-source-label">Cloning from:</div>
              <div className="fw-clone-source-name">{form.sourceName}</div>
            </div>
            <div className="fw-modal-field">
              <label htmlFor="fw-clone-name">New Framework Name *</label>
              <input
                id="fw-clone-name"
                type="text"
                placeholder="e.g., Custom Healthcare Framework"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="fw-modal-field">
              <label htmlFor="fw-clone-desc">Description</label>
              <textarea
                id="fw-clone-desc"
                placeholder="Optional description..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="fw-modal-actions">
              <button type="button" className="fw-modal-cancel" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="fw-modal-confirm fw-modal-confirm-clone"
                onClick={() => run(() => pdApi.settings.cloneFramework(form.id))}
              >
                Clone Framework
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ---- Edit ---- */}
      {modal === 'edit' ? (
        <div className="fw-modal-backdrop" onClick={() => setModal(null)}>
          <div className="fw-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Framework</h3>
            <div className="fw-modal-field">
              <label htmlFor="fw-edit-name">Framework Name *</label>
              <input
                id="fw-edit-name"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="fw-modal-field">
              <label htmlFor="fw-edit-desc">Description</label>
              <textarea
                id="fw-edit-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="fw-modal-actions">
              <button type="button" className="fw-modal-cancel" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="fw-modal-confirm fw-modal-confirm-edit"
                onClick={() =>
                  run(() =>
                    pdApi.settings.updateFramework(form.id, {
                      framework_name: form.name,
                      framework_description: form.description,
                    }),
                  )
                }
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
